import { useTranslation } from 'react-i18next'
import LoadingIndicator from '@/widgets/LoadingIndicator'
import NoResultsIndicator from '@/widgets/NoResultsIndicator'
import { getFilterIndicator, getSortIndicator } from '@/lib/tableHelpers'
import ProjectsTableRow from './ProjectsTableRow'

export default function ProjectsTable({
	projectsData,
	loading,
	sortColumn,
	sortDirection,
	onSort,
	hasStartDateFilter,
	onOpenStartDateFilter,
	hasTrenerFilter,
	onOpenTrenerFilter,
	onDelete,
	onDuplicate,
}) {
	const { t } = useTranslation()
	const rows = Array.isArray(projectsData) ? projectsData : []

	const columns = [
		{
			key: 'data_rozpoczecia',
			label: t('projects.list.table.startDate', { defaultValue: 'Data rozpoczęcia' }),
			sortable: true,
			hasFilter: hasStartDateFilter,
			onOpenFilter: onOpenStartDateFilter,
		},
		{
			key: 'nazwa',
			label: t('projects.list.table.projectName', { defaultValue: t('projects.list.table.trainingName') }),
			sortable: true,
		},
		{
			key: 'trainerName',
			label: t('projects.list.table.trainerName', { defaultValue: 'Trener' }),
			sortable: false,
			hasFilter: hasTrenerFilter,
			onOpenFilter: onOpenTrenerFilter,
		},
	]

	const renderColumnContent = column => {
		const columnLabel = column.sortable ? (
			<button
				type="button"
				className="flex items-center gap-1 font-semibold text-gray-700 hover:text-blue-600 transition-colors duration-200 focus:outline-none focus:text-blue-600"
				onClick={() => onSort(column.key)}
			>
				{column.label}
				{getSortIndicator(column.key, sortColumn, sortDirection)}
			</button>
		) : (
			<span className="font-semibold text-gray-700">{column.label}</span>
		)

		if (!column.onOpenFilter) return columnLabel

		return (
			<div className="inline-flex items-center gap-1">
				{columnLabel}
				<button
					type="button"
					className="ml-2 p-1 hover:bg-gray-100 rounded transition-colors duration-200 focus:outline-none focus:bg-gray-100"
					onClick={column.onOpenFilter}
					title={t('projects.list.table.filterColumn')}
				>
					{getFilterIndicator(column.hasFilter)}
				</button>
			</div>
		)
	}

	if (loading) return <LoadingIndicator />

	return (
		<div className="mt-6 overflow-x-auto">
			<table className="min-w-full bg-white border border-gray-300 rounded-lg shadow">
				<thead>
					<tr>
						{columns.map(column => (
							<th key={column.key} className="py-2 px-4 border-b border-gray-300 text-left">
								{renderColumnContent(column)}
							</th>
						))}
						<th className="py-2 px-4 border-b border-gray-300 text-right w-24">
							<span className="font-semibold text-gray-700">{t('projects.list.table.actions')}</span>
						</th>
					</tr>
				</thead>
				<tbody>
					{rows.map(project => (
						<ProjectsTableRow
							key={project._id}
							project={project}
							onDelete={onDelete}
							onDuplicate={onDuplicate}
						/>
					))}
				</tbody>
			</table>
			{rows.length === 0 ? (
				<div className="py-4">
					<NoResultsIndicator
						message={t('projects.list.emptyState.title')}
						description={t('projects.list.emptyState.description')}
					/>
				</div>
			) : null}
		</div>
	)
}
